import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { formatDateTime } from '@/lib/format';
import { fetchEmailLastEvent, type EmailLastEvent } from '@/lib/emailLogs';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import type { EmailLogEntry } from '@/lib/firestoreTenantDetail';

type Props = {
  entries: EmailLogEntry[];
};

const mono = (v: React.ReactNode): React.JSX.Element => (
  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
);

type LastEventState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; event: EmailLastEvent | null }
  | { kind: 'ERROR' };

/**
 * Célula "Último evento": lê o evento mais recente da subcoleção
 * `/emailLogs/{id}/events` (`fetchEmailLastEvent`).
 *
 * Origem: #795 — 2026-10-03 14:12 +0200 (ADM-5).
 *
 * Por quê: o último evento não está no doc da linha de forma confiável (o
 * `lastEvent` do doc pai é o último a CHEGAR, ver `lib/emailLogs.ts`), e a
 * versão anterior lia um array `events[]` que nunca existiu — a coluna era
 * sempre "—".
 *
 * Contrato: sem evento ainda (`receivedAt === null`) → "—" sem leitura. Com
 * evento → 1 leitura; relê quando `receivedAt` muda (chegou outro evento).
 * Falha → "—" e `onError` (a tabela reporta uma vez).
 *
 * Usado por: `EmailLogsTable` (abaixo).
 */
function LastEventCell({
  logId,
  receivedAt,
  lang,
  onError,
}: {
  logId: string;
  receivedAt: number | null;
  lang: string;
  onError: (err: unknown) => void;
}): React.JSX.Element {
  const [state, setState] = useState<LastEventState>({ kind: 'LOADING' });

  useEffect(() => {
    if (receivedAt === null) {
      setState({ kind: 'READY', event: null });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'LOADING' });
    fetchEmailLastEvent(logId).then(
      (event) => {
        if (!cancelled) setState({ kind: 'READY', event });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'ERROR' });
        onError(err);
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [logId, receivedAt, onError]);

  if (state.kind === 'LOADING') return <Spinner />;
  if (state.kind === 'ERROR' || state.event === null) return <>—</>;
  return mono(
    <>
      {state.event.event}{' '}
      <Box variant="span" color="text-status-inactive">
        {formatDateTime(state.event.at, lang)}
      </Box>
    </>,
  );
}

/**
 * Tabela da aba Logs de `/billing/emails`.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (rotas /billing/*, Sprint 16).
 *
 * Contrato: recebe as linhas já mapeadas por `subscribeEmailLogsByTenant`;
 * colunas quando, template, destinatário, idioma, empresa e último evento.
 * Falha ao ler o último evento é reportada UMA vez por montagem
 * (`admin_email_last_event_failed`) — 100 linhas falhando pelo mesmo motivo
 * não viram 100 eventos.
 *
 * Usado por: `routes/BillingEmailsPage.tsx` (web `apps/admin`, super-admin).
 *
 * Falhas conhecidas:
 *  - 2026-05-05 → 2026-10-03 (#795, ADM-5): template/idioma/empresa e último
 *    evento eram sempre "—" (campos lidos de `custom_args`/`events[]`, que o
 *    doc não tem).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-5) último evento lido da subcoleção.
 */
export function EmailLogsTable({ entries }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const reported = useRef(false);
  const onLastEventError = useCallback((err: unknown): void => {
    if (reported.current) return;
    reported.current = true;
    reportNonFatal('admin_email_last_event_failed', serializeError(err));
  }, []);

  return (
    <Table<EmailLogEntry>
      items={entries}
      trackBy="id"
      variant="embedded"
      empty={<Box textAlign="center" color="inherit">{t('billing.emails.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'createdAt',
          header: t('billing.emails.columns.createdAt'),
          cell: (e): React.ReactNode =>
            e.createdAt !== null ? formatDateTime(e.createdAt, lang) : '—',
        },
        {
          id: 'template',
          header: t('billing.emails.columns.template'),
          cell: (e): React.ReactNode => mono(e.templateId ?? '—'),
        },
        {
          id: 'to',
          header: t('billing.emails.columns.to'),
          cell: (e): React.ReactNode => e.to ?? '—',
        },
        {
          id: 'locale',
          header: t('billing.emails.columns.locale'),
          cell: (e): React.ReactNode => mono(e.locale ?? '—'),
        },
        {
          id: 'tenant',
          header: t('billing.emails.columns.tenant'),
          cell: (e): React.ReactNode => mono(e.tenantId ?? '—'),
        },
        {
          id: 'lastEvent',
          header: t('billing.emails.columns.lastEvent'),
          cell: (e): React.ReactNode => (
            <LastEventCell
              logId={e.id}
              receivedAt={e.lastEventReceivedAt}
              lang={lang}
              onError={onLastEventError}
            />
          ),
        },
      ]}
    />
  );
}
